import { useState, type ReactNode } from "react";
import type { PracticeCurrentResponse, PracticeView } from "@pushback-and-chaos/contracts";
import { PRACTICE } from "../design/copy.js";
import { Pill } from "../ui/primitives.js";
import { FreeTextInput } from "../ui/input.js";

/**
 * THE PRACTICE EXERCISE, IN TWO PARTS (coach plan steps 6 to 8; C09, C06,
 * C07; re-cut for the desk on 2026-09-23). What it shows is the server's
 * projection of the exercise's events; every control here sends one event
 * the engine checks, and the surface re-reads. Nothing on it is a number,
 * nothing on it is a reading of the record, and finishing an exercise says
 * so in the brief's own words, never as a mastery claim.
 *
 * `PracticeBody` is the session: the offer, the situation and task, the
 * criteria with their status in words, the attempts, the reflection, the
 * revision, and the controls. `PracticeComposer` is the one text box, kept
 * apart so the expanded practice card can pin it at its foot while the body
 * scrolls. `PracticePanel` is the two together, for a surface that wants
 * them in one column.
 *
 * Resumable by construction: the state lives on the server, so a reload or
 * a return visit shows the same exercise where it was left, paused or not.
 */
export type PracticeAction = "accept" | "decline" | "pause" | "resume" | "conclude" | "hint" | "example" | "variation" | "revision";

export interface PracticeBodyProps {
  /** The exercise this surface shows, or null for the chooser: the library on offer. */
  readonly exercise: PracticeView | null;
  readonly available: PracticeCurrentResponse["available"];
  readonly busy: boolean;
  readonly onOffer: (briefId?: string) => void;
  readonly onAction: (exerciseId: string, action: PracticeAction) => void;
}

export interface PracticeComposerProps {
  readonly exercise: PracticeView | null;
  readonly busy: boolean;
  readonly onAttempt: (exerciseId: string, text: string, revises: number | null) => void;
  readonly onOpenSettings: () => void;
}

export interface PracticePanelProps extends Omit<PracticeBodyProps, "exercise" | "available">, Omit<PracticeComposerProps, "exercise"> {
  readonly practice: PracticeCurrentResponse | null;
}

/** The latest attempt an exercise holds, for what the composer's next line revises. */
function latestAttempt(current: PracticeView): PracticeView["state"]["attempts"][number] | null {
  return current.state.attempts.at(-1) ?? null;
}

/** Whether the composer has anything to send to: an accepted or attempted exercise, not paused, still open to an attempt. */
export function practiceCanWrite(exercise: PracticeView | null): boolean {
  if (exercise === null) return false;
  return exercise.state.stage !== "PAUSED" && exercise.can.attempt;
}

export function PracticeBody({ exercise, available, busy, onOffer, onAction }: PracticeBodyProps): ReactNode {
  const [choosing, setChoosing] = useState(false);
  const current = exercise;

  if (current === null) {
    return (
      <div data-testid="practice-panel" className="pc-practice">
        <p className="m-0 mb-3 font-sans text-small">{PRACTICE.none}</p>
        {choosing ? (
          <div className="flex flex-wrap gap-3">
            {available.map((b) => (
              <Pill key={b.briefId} onClick={() => { setChoosing(false); onOffer(b.briefId); }} disabled={busy} title={b.targetBehaviour}>
                {b.title}
              </Pill>
            ))}
            <Pill onClick={() => setChoosing(false)} disabled={busy}>{PRACTICE.chooseCancel}</Pill>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            <Pill onClick={() => onOffer()} disabled={busy}>{PRACTICE.offerOne}</Pill>
            <Pill onClick={() => setChoosing(true)} disabled={busy || available.length === 0}>{PRACTICE.choose}</Pill>
          </div>
        )}
      </div>
    );
  }

  const { brief, state, reflection, can } = current;
  const id = current.exerciseId;
  const act = (action: PracticeAction) => () => onAction(id, action);

  return (
    <div data-testid="practice-panel" data-stage={state.stage} className="pc-practice">
      <p className="pcs-quiet m-0 mb-1">{stageWord(state.stage, state.finished)}</p>
      <p className="m-0 mb-2 font-display text-body font-bold">{brief.title}</p>
      <p className="m-0 mb-3 font-sans text-small">{brief.target_behaviour}</p>

      {state.stage === "OFFERED" ? (
        <>
          <p className="m-0 mb-3 font-sans text-small text-ink-soft">{brief.why}</p>
          <div className="flex flex-wrap gap-3">
            <Pill onClick={act("accept")} disabled={busy}>{PRACTICE.accept}</Pill>
            <Pill onClick={act("decline")} disabled={busy}>{PRACTICE.decline}</Pill>
          </div>
        </>
      ) : null}

      {state.stage === "ACCEPTED" || state.stage === "ATTEMPTED" || state.stage === "PAUSED" ? (
        <>
          <p className="m-0 mb-2 font-sans text-small whitespace-pre-line">{brief.situation}</p>
          <p className="m-0 mb-2 font-sans text-small"><span className="font-bold">{PRACTICE.task}</span> {brief.task}</p>
          <ul data-testid="practice-criteria" className="m-0 mb-3 list-none p-0 font-sans text-small">
            {brief.criteria.map((c) => (
              <li key={c.id} data-status={state.criteria[c.id] ?? "UNREAD"} className="mb-1">
                {statusMark(state.criteria[c.id] ?? "UNREAD")} {c.text}
              </li>
            ))}
          </ul>
          {state.hint_shown ? <p className="m-0 mb-2 font-sans text-small text-ink-soft"><span className="font-bold">{PRACTICE.hintLabel}</span> {brief.hint}</p> : null}
          {state.example_shown ? <p className="m-0 mb-2 font-sans text-small text-ink-soft"><span className="font-bold">{PRACTICE.exampleLabel}</span> {brief.example}</p> : null}

          {state.attempts.map((a) => (
            <div key={a.index} className="mb-3">
              <p className="pcs-said m-0">{a.text}</p>
              {a.counterpart_line !== null && brief.counterpart !== null ? (
                <p className="m-0 mt-1 font-sans text-small"><span className="font-bold">{brief.counterpart.name}:</span> {a.counterpart_line}</p>
              ) : null}
            </div>
          ))}
          {reflection.length > 0 ? (
            <div data-testid="practice-reflection" className="mb-3">
              {reflection.map((line) => (
                <p key={line.slice(0, 48)} className="pcs-coach m-0 mb-1">{line}</p>
              ))}
            </div>
          ) : null}
          {state.revision !== null ? (
            <div data-testid="practice-revision" className="mb-3 border-l-2 border-l-grid-line-major pl-3">
              <p className="pcs-quiet m-0 mb-1">{state.revision.source === "model" ? PRACTICE.revisionLabel : PRACTICE.exampleInstead}</p>
              <p className="m-0 mb-1 font-sans text-small">{state.revision.revision}</p>
              <p className="m-0 font-sans text-small text-ink-soft">{state.revision.change}</p>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-3">
            {can.hint ? <Pill onClick={act("hint")} disabled={busy}>{PRACTICE.hint}</Pill> : null}
            {can.example ? <Pill onClick={act("example")} disabled={busy}>{PRACTICE.example}</Pill> : null}
            {can.revision ? <Pill onClick={act("revision")} disabled={busy}>{PRACTICE.revision}</Pill> : null}
            {can.variation ? <Pill onClick={act("variation")} disabled={busy}>{PRACTICE.variation}</Pill> : null}
            {can.pause ? <Pill onClick={act("pause")} disabled={busy}>{PRACTICE.pause}</Pill> : null}
            {can.resume ? <Pill onClick={act("resume")} disabled={busy}>{PRACTICE.resume}</Pill> : null}
            {can.decline ? <Pill onClick={act("decline")} disabled={busy}>{PRACTICE.decline}</Pill> : null}
            {can.conclude ? <Pill onClick={act("conclude")} disabled={busy}>{state.finished ? PRACTICE.concludeDone : PRACTICE.concludeEarly}</Pill> : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

/** The one text box of a practice session: an attempt, or the next attempt revising the last. Rendered only while the exercise can take one. */
export function PracticeComposer({ exercise, busy, onAttempt, onOpenSettings }: PracticeComposerProps): ReactNode {
  const [draft, setDraft] = useState("");
  const current = exercise;
  if (current === null || !practiceCanWrite(current)) return null;
  const latest = latestAttempt(current);
  const id = current.exerciseId;
  return (
    <div data-testid="practice-composer">
      <FreeTextInput
        placeholder={latest === null ? PRACTICE.attemptPlaceholder : PRACTICE.revisePlaceholder}
        disabled={busy}
        disabledReason={busy ? PRACTICE.reading : undefined}
        onSubmit={(text) => { setDraft(""); onAttempt(id, text, latest === null ? null : latest.index); }}
        onOpenSettings={onOpenSettings}
        draft={draft}
        onDraftChange={setDraft}
        compact
      />
    </div>
  );
}

/** Body and composer in one column, for a surface without a foot to pin the box to. */
export function PracticePanel(props: PracticePanelProps): ReactNode {
  if (props.practice === null) return null;
  const exercise = props.practice.current ?? props.practice.offers[0] ?? null;
  return (
    <div className="mt-6">
      <p className="pcs-quiet m-0 mb-2">{PRACTICE.label}</p>
      <PracticeBody exercise={exercise} available={props.practice.available} busy={props.busy} onOffer={props.onOffer} onAction={props.onAction} />
      <div className="mt-3">
        <PracticeComposer exercise={exercise} busy={props.busy} onAttempt={props.onAttempt} onOpenSettings={props.onOpenSettings} />
      </div>
    </div>
  );
}

export function stageWord(stage: PracticeView["state"]["stage"], finished: boolean): string {
  if (finished && (stage === "ATTEMPTED" || stage === "PAUSED")) return PRACTICE.stageFinished;
  return PRACTICE.stages[stage];
}

function statusMark(status: "PRESENT" | "NOT_FOUND" | "UNREAD"): string {
  return status === "PRESENT" ? PRACTICE.markPresent : status === "NOT_FOUND" ? PRACTICE.markMissing : PRACTICE.markUnread;
}
